// src/components/Shipping/hooks/useShippingData.js
import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for fetching and filtering shipping data from backend
 * @param {Object} initialFilters
 * @returns {Object} { shipments, loading, error, refresh, updateFilters }
 */
export function useShippingData(initialFilters = {}) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(initialFilters);

  // Fetch data from API
  const fetchData = useCallback(
    async (customFilters = filters) => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        Object.entries(customFilters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });

        const url = `${process.env.REACT_APP_API_URL}/api/shipping?${params.toString()}`;
        console.log('📡 Fetching shipping data:', url);

        const res = await fetch(url);
        const data = await res.json();

        if (!res.ok) throw new Error(data.error || 'Failed to fetch shipping data');
        setShipments(data.results || []);
      } catch (err) {
        console.error('❌ Shipping fetch error:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Exposed functions
  return {
    shipments,
    loading,
    error,
    refresh: () => fetchData(),
    updateFilters: (newFilters) => {
      const merged = { ...filters, ...newFilters };
      setFilters(merged);
      fetchData(merged);
    },
  };
}
